// src/components/AIAssistantManagement.js

import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './AIAssistantManagement.css';
import CreateForm from './CreateForm';
import KnowledgeBoard from './KnowledgeBoard';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaRobot, FaPen, FaCode, FaTrash, FaPlay, FaPlus, FaBook, FaWpforms, FaFilePdf, FaToggleOn, FaToggleOff, FaTools, FaCheck, FaUsers, FaTruck, FaCommentDots, FaTimes, FaWarehouse } from 'react-icons/fa';
import Button from './UI/Button';
import CreatePDFFormFiller from './CreatePDFFormFiller';
import OpenAPISchemeManagement from './OpenAPISchemeManagement';
import ChatbotTest from './ChatbotTest';
import ClientsInstructions from './ClientsInstructions';
import CarriersInstructions from './CarriersInstructions';
import WarehousesInstructions from './WarehousesInstructions';

const AIAssistantManagement = () => {
  const { t } = useTranslation();
  const [user, setUser] = useState(undefined);
  const [aiAssistants, setAIAssistants] = useState([]);
  const [selectedAssistant, setSelectedAssistant] = useState(null);
  const [name, setName] = useState('');
  const [prompt, setPrompt] = useState('');
  const [questions, setQuestions] = useState(['', '', '', '']);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [showPopup, setShowPopup] = useState(false);
  const [promptPopupOpen, setPromptPopupOpen] = useState(false);
  const [promptInput, setPromptInput] = useState('');
  const [promptMode, setPromptMode] = useState('edit');
  const [activeTab, setActiveTab] = useState('carriersInstructions');
  const [availableTools, setAvailableTools] = useState([]);
  const [selectedToolIds, setSelectedToolIds] = useState([]);
  const navigate = useNavigate();
  const [automation, setAutomation] = useState(false);
  const [showAgentSelector, setShowAgentSelector] = useState(false);
  const [showCreateAgentModal, setShowCreateAgentModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [editedPrompt, setEditedPrompt] = useState('');
  const [editedToolIds, setEditedToolIds] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showPromptModal, setShowPromptModal] = useState(false);
  const [showToolsOverlay, setShowToolsOverlay] = useState(false);
  const [showChatBubble, setShowChatBubble] = useState(false);
  const chatbotTestRef = useRef(null);

  const questionTitles = [
    t('aiAssistant.question1'),
    t('aiAssistant.question2'),
    t('aiAssistant.question3'),
    t('aiAssistant.question4'),
  ];

  // --- Dummy Data for "experience" role ---
  const dummyAIAssistantsData = [
    { _id: 'exp_asst_1', name: 'Customer Service Pro (Demo)', prompt: 'You are a friendly and efficient customer service AI. Help users with their inquiries about orders, returns, and product information. Prioritize quick and accurate solutions.', selected_tool_ids: ['exp_tool_1', 'exp_tool_2'], is_running: true, created_at: new Date().toISOString(), updated_at: new Date().toISOString() },
    { _id: 'exp_asst_2', name: 'Technical Support Bot (Demo)', prompt: 'Assist users with technical issues, troubleshooting, and setup guides. Be patient and provide clear, step-by-step instructions.', selected_tool_ids: ['exp_tool_3'], is_running: false, created_at: new Date().toISOString(), updated_at: new Date().toISOString() },
    { _id: 'exp_asst_3', name: 'Sales Advisor (Demo)', prompt: 'Guide customers through our product catalog, offer recommendations, and help them complete purchases. Focus on upselling and cross-selling opportunities.', selected_tool_ids: [], is_running: false, created_at: new Date().toISOString(), updated_at: new Date().toISOString() },
  ];

  const dummyAvailableToolsData = [
    { id: 'exp_tool_1', name: 'get_tracking_info', description: 'Retrieves tracking information for an order.' },
    { id: 'exp_tool_2', name: 'Retrieve_customer_order_by_ID', description: 'Fetches order details using the order ID.' },
    { id: 'exp_tool_3', name: 'retrieve_knowledge', description: 'Searches the knowledge base for answers.' },
    { id: 'exp_tool_4', name: 'assign_to_employee', description: 'Assigns the ticket to a human agent.' },
  ];
  // --- End of Dummy Data ---

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      navigate('/');
      return;
    }
    setIsLoading(true); // Set loading true at the beginning
    fetchUserData(token);
    
    const urlParams = new URLSearchParams(window.location.search);
    const assistantIdFromUrl = urlParams.get('assistantId');
    if (assistantIdFromUrl) {
      localStorage.setItem('pendingAssistantSelection', assistantIdFromUrl);
    }
  }, [navigate]);

  useEffect(() => {
    if (user === null) { // If fetchUserData explicitly sets user to null (error)
      navigate('/');
    } else if (user) { // User data is fetched
      if (user.role === 'experience') {
        loadExperienceModeData();
      } else {
        const token = localStorage.getItem('access_token');
        fetchData(token); // Fetch real data for other roles
      }
      // Set automation status based on user profile or default for experience
      setAutomation(user.role === 'experience' ? true : (user.automation || false));
    }
  }, [user]);

  useEffect(() => {
    if (user && (activeTab === 'form' || activeTab === 'pdfFormFiller')) {
      setActiveTab('carriersInstructions');
    }
  }, [user, activeTab]);


  const loadExperienceModeData = () => {
    setAIAssistants(dummyAIAssistantsData);
    setAvailableTools(dummyAvailableToolsData);

    const pendingAssistantId = localStorage.getItem('pendingAssistantSelection');
    let assistantToSelect = null;

    if (pendingAssistantId) {
      assistantToSelect = dummyAIAssistantsData.find(a => a._id === pendingAssistantId);
      localStorage.removeItem('pendingAssistantSelection');
    }
    
    if (!assistantToSelect) {
      assistantToSelect = dummyAIAssistantsData.find(a => a.is_running) || dummyAIAssistantsData[0];
    }

    if (assistantToSelect) {
      setSelectedAssistant(assistantToSelect);
    }
    setIsLoading(false);
  };


  useEffect(() => {
    if (selectedAssistant) {
      setName(selectedAssistant.name);
      setPrompt(selectedAssistant.prompt);
      setSelectedToolIds(selectedAssistant.selected_tool_ids || []);
      setEditedName(selectedAssistant.name);
      setEditedPrompt(selectedAssistant.prompt);
      setEditedToolIds(selectedAssistant.selected_tool_ids || []);
    } else {
      // If no assistant is selected (e.g. after deleting the selected one in experience mode)
      // reset relevant form fields or select another default if applicable.
       if (user && user.role === 'experience' && aiAssistants.length > 0) {
         const defaultAssistant = aiAssistants.find(a => a.is_running) || aiAssistants[0];
         if(defaultAssistant) setSelectedAssistant(defaultAssistant);
         else resetForm(); // Or handle no assistants case
       } else if (!user || user.role !== 'experience') {
          // For non-experience users, if selectedAssistant becomes null, reset or handle as per existing logic
          // This part might need adjustment based on how non-experience users expect it to behave when selectedAssistant is null
       }
    }
  }, [selectedAssistant, user, aiAssistants]); // Added user and aiAssistants to dependencies

  const fetchUserData = async (token) => {
    try {
      const response = await axios.get('/api/users/user', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user data:', error);
      setUser(null); // Set user to null on error to trigger redirect or stop loading
      setIsLoading(false);
    }
  };

  const fetchData = async (token) => {
    // This function is now only for non-experience users
    if (user && user.role === 'experience') {
      setIsLoading(false); // Should already be handled by loadExperienceModeData
      return;
    }
    try {
      // setIsLoading(true) is managed by the calling useEffect or initial state
      const [assistantsResponse, toolsResponse] = await Promise.all([
        axios.get('/api/ai-assistants', {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get('/api/tools', {
          headers: { Authorization: `Bearer ${token}` },
        })
      ]);

      const assistants = assistantsResponse.data;
      setAIAssistants(assistants);
      
      const pendingAssistantId = localStorage.getItem('pendingAssistantSelection');
      if (pendingAssistantId) {
        const assistantToSelect = assistants.find(assistant => assistant._id === pendingAssistantId);
        if (assistantToSelect) {
          setSelectedAssistant(assistantToSelect);
        }
        localStorage.removeItem('pendingAssistantSelection');
      } else {
        const runningAgent = assistants.find(assistant => assistant.is_running);
        if (runningAgent) {
          setSelectedAssistant(runningAgent);
        } else if (assistants.length > 0) {
          setSelectedAssistant(assistants[0]); // Fallback to first if no running
        }
      }
      
      setAvailableTools(toolsResponse.data);
      setIsLoading(false);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      setIsLoading(false);
    }
  };


  const handleDeleteAIAssistant = async (assistantIdToDelete) => {
    if (!assistantIdToDelete) {
      console.error('Assistant ID is undefined');
      alert('Unable to delete AI assistant: Missing ID');
      return;
    }

    if (user && user.role === 'experience') {
      setAIAssistants(prev => prev.filter(a => a._id !== assistantIdToDelete));
      if (selectedAssistant && selectedAssistant._id === assistantIdToDelete) {
        // If the deleted assistant was selected, select another one or reset
        const remainingAssistants = aiAssistants.filter(a => a._id !== assistantIdToDelete);
        if (remainingAssistants.length > 0) {
            setSelectedAssistant(remainingAssistants.find(a => a.is_running) || remainingAssistants[0]);
        } else {
            resetForm(); // No more assistants left
        }
      }
      alert('AI assistant (Demo) deleted successfully');
      return;
    }
    
    try {
      const token = localStorage.getItem('access_token');
      await axios.delete(`/api/ai-assistants/${assistantIdToDelete}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      alert('AI assistant deleted successfully');
      fetchData(token); // Re-fetch real data
    } catch (error) {
      console.error('Failed to delete AI assistant:', error);
      alert('Failed to delete AI assistant.');
    }
  };

  const handleCreateAIAssistant = async () => {
    if (user && user.role === 'experience') {
      const newDummyAssistant = {
        _id: `exp_asst_${Date.now()}`,
        name: name || 'New Demo Agent',
        prompt: prompt || 'Default demo prompt.',
        selected_tool_ids: selectedToolIds || [],
        is_running: false, // New agents aren't running by default
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      setAIAssistants(prev => [...prev, newDummyAssistant]);
      setSelectedAssistant(newDummyAssistant); // Select the newly created dummy assistant
      resetForm(); // Reset form fields for next creation, but name/prompt/tools are from current state
      setShowCreateAgentModal(false); // Close modal after creation
      alert('AI assistant (Demo) created successfully');
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(
        '/api/ai-assistants',
        { name, prompt, selected_tool_ids: selectedToolIds },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchData(token); // Re-fetch real data
      setSelectedAssistant(response.data); // Select the newly created assistant
      resetForm();
      setShowCreateAgentModal(false); // Close modal
      alert('AI assistant created successfully');
    } catch (error) {
      if (error.response && error.response.status === 400) {
        console.error('Failed to create AI assistant:', error.response.data.detail);
        alert(error.response.data.detail);
      } else {
        console.error('Failed to create AI assistant:', error);
        alert('Failed to create AI assistant.');
      }
    }
  };

  const handleUpdateAIAssistant = async () => {
    if (!selectedAssistant) {
      alert(t('aiAssistant.noAgentSelectedUpdate'));
      return;
    }
    if (user && user.role === 'experience') {
      const updatedAssistants = aiAssistants.map(a =>
        a._id === selectedAssistant._id
          ? { ...a, name: editedName, prompt: editedPrompt, selected_tool_ids: editedToolIds, updated_at: new Date().toISOString() }
          : a
      );
      setAIAssistants(updatedAssistants);
      const newlyUpdatedAssistant = updatedAssistants.find(a => a._id === selectedAssistant._id);
      setSelectedAssistant(newlyUpdatedAssistant); // Update selected assistant with new details
      setName(editedName); // Sync top-level state if needed, though selectedAssistant change should trigger it
      setPrompt(editedPrompt);
      setSelectedToolIds(editedToolIds);
      setIsEditing(false);
      alert(t('aiAssistant.updateSuccessDemo'));
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.put(
        `/api/ai-assistants/${selectedAssistant._id}`,
        { 
          name: editedName,
          prompt: editedPrompt,
          selected_tool_ids: editedToolIds 
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // For real updates, fetchData will refresh the list and selectedAssistant if needed
      fetchData(token).then(() => {
         // Ensure the selected assistant is updated in the state after fetching
        const updatedAssistantFromServer = aiAssistants.find(a => a._id === selectedAssistant._id) || response.data;
        if(updatedAssistantFromServer){
            setSelectedAssistant(updatedAssistantFromServer);
        }
      });
      setIsEditing(false);
      alert(t('aiAssistant.updateSuccess'));
    } catch (error) {
      console.error('Failed to update AI assistant:', error);
      alert(t('aiAssistant.updateError'));
    }
  };

  const handleSelectAssistant = (assistant) => {
    // This function is primarily for UI selection, data is already loaded (dummy or real)
    setSelectedAssistant(assistant);
    // Form fields will update via the useEffect hook that depends on selectedAssistant
    setIsEditing(false); // Exit editing mode when switching assistants
  };

  const handleSetRunning = async (assistantIdToRun) => {
    if (user && user.role === 'experience') {
      const updatedAssistants = aiAssistants.map(a => ({
        ...a,
        is_running: a._id === assistantIdToRun,
      }));
      setAIAssistants(updatedAssistants);
      // If the currently selected assistant's running status changed, update it
      if (selectedAssistant && selectedAssistant._id === assistantIdToRun) {
        setSelectedAssistant(prev => ({...prev, is_running: true}));
      } else if (selectedAssistant && selectedAssistant.is_running && selectedAssistant._id !== assistantIdToRun){
         setSelectedAssistant(prev => ({...prev, is_running: false}));
      }
      // Update the selected assistant if it's the one being run
      const newlyRunAssistant = updatedAssistants.find(a => a._id === assistantIdToRun);
      if (newlyRunAssistant && selectedAssistant && selectedAssistant._id === assistantIdToRun) {
        // setSelectedAssistant(newlyRunAssistant); // This might cause loop if selectedAssistant effect is not careful
      } else if (newlyRunAssistant) {
        // If a different assistant is made running, the currently selected one might need its is_running flag updated in state
        const currentSelected = updatedAssistants.find(a => a._id === (selectedAssistant ? selectedAssistant._id : null));
        if (currentSelected) setSelectedAssistant(currentSelected);
      }
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      await axios.put(
        `/api/ai-assistants/${assistantIdToRun}/running`,
        null,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchData(token); // Re-fetch to get updated running status
    } catch (error) {
      console.error('Failed to set AI assistant as running:', error);
      alert('Failed to set AI assistant as running.');
    }
  };
  
  const resetForm = () => {
    // When resetting form for creation or after deletion
    // setSelectedAssistant(null); // This might be too disruptive if user wants to create multiple
    setName('');
    setPrompt('');
    setSelectedToolIds([]);
    setPromptInput(''); // If this is for prompt generation popup
    
    // For editing state, ensure these are also reset or handled
    setEditedName('');
    setEditedPrompt('');
    setEditedToolIds([]);
    setIsEditing(false);
  };


  const handleOpenPopup = () => {
    setShowPopup(true);
    setCurrentQuestionIndex(0);
  };

  const handleClosePopup = () => {
    setShowPopup(false);
    setCurrentQuestionIndex(0);
  };

  const handleQuestionChange = (index, value) => {
    const updatedQuestions = [...questions];
    updatedQuestions[index] = value;
    setQuestions(updatedQuestions);
  };

  const handleNextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handlePromptPopupOpen = () => {
    setPromptInput(prompt);
    setPromptMode('edit');
    setPromptPopupOpen(true);
  };

  const handlePromptPopupClose = () => {
    setPromptPopupOpen(false);
    setPromptMode('edit');
  };

  const handlePromptSave = () => {
    setPrompt(promptInput);
    handlePromptPopupClose();
  };

  const handleSwitchToGenerate = () => {
    setPromptMode('generate');
    setQuestions(['', '', '', '']);
  };

  const handleGenerateInstructions = async () => {
    if (user && user.role === 'experience') {
      // Simulate instruction generation
      const generatedPrompt = `Based on your answers (Q1: ${questions[0]}, Q2: ${questions[1]}, etc.), here are your generated demo instructions for the AI. This is a placeholder.`;
      setPromptInput(generatedPrompt);
      setPromptMode('edit'); // Switch to edit mode to show the generated prompt
      alert('Demo instructions generated!');
      return;
    }
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(
        '/api/ai-assistants/generate-instructions',
        { questions },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      setPromptInput(response.data.instructions);
      setPromptMode('edit');
    } catch (error) {
      console.error('Failed to generate instructions:', error);
      alert('Failed to generate instructions.');
    }
  };

  const handlePromptChange = (e) => {
    setPromptInput(e.target.value);
  };

  const getPromptPreview = (text) => {
    const maxLength = 50;
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'knowledge':
        return <KnowledgeBoard />;
      case 'codeSnippets':
        return <OpenAPISchemeManagement />;
      case 'chatbotTest':
        return <ChatbotTest />;
      case 'clientsInstructions':
        return <ClientsInstructions />;
      case 'carriersInstructions':
        return <CarriersInstructions />;
      case 'warehousesInstructions':
        return <WarehousesInstructions />;
      case 'test':
        return (
          <div className="test-tab-container">
            <ChatbotTest 
              assistantId={selectedAssistant?._id}
              assistantName={selectedAssistant?.name}
            />
          </div>
        );
      case 'prompt':
        return (
          <div className="prompt-tab-container">
            {isEditing ? (
              <textarea
                value={editedPrompt}
                onChange={(e) => setEditedPrompt(e.target.value)}
                rows="15"
                className="ai-agent-textarea"
              />
            ) : (
              <div className="prompt-preview-content">
                {prompt}
              </div>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  const handleAutomationToggle = async () => {
    const newAutomationState = !automation;
    if (user && user.role === 'experience') {
      setAutomation(newAutomationState);
      // Optionally update the dummy user object if it were in state, but here just local state
      alert(`Demo automation ${newAutomationState ? 'enabled' : 'disabled'}.`);
      return;
    }
    try {
      const token = localStorage.getItem('access_token');
      await axios.put(
        '/api/users/automation',
        { automation: newAutomationState },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAutomation(newAutomationState);
    } catch (error) {
      console.error('Failed to toggle automation:', error);
      alert('Failed to toggle automation.');
    }
  };

  const handleAgentCircleClick = () => {
    setShowAgentSelector(true);
  };

  const getToolTranslation = (toolName) => {
    const toolTranslations = {
      'retrieve_knowledge': t('tools.retrieveKnowledge'),
      'assign_to_employee': t('tools.assignToEmployee'),
      'resolve_ticket': t('tools.resolveTicket'),
      'Close_ticket': t('tools.closeTicket'),
      'get_tracking_info': t('tools.getTrackingInfo'),
      'Retrieve_customer_order_by_reference_and_email': t('tools.retrieveOrder'),
      'Update_customer_shipping_address': t('tools.updateShippingAddress'),
      'find_relay_points': t('tools.findRelayPoints'),
      'create_return_with_edn_label': t('tools.createReturn'),
      'carriers_conversation': t('tools.carriersConversation'),
      'Retour': t('tools.return'),
      'fill_form_fields': t('tools.fillPDF'),
      'getOrderById': t('tools.getOrderById'),
      'carriers_conversation_update': t('tools.carriersConversationUpdate'),
      'order_status': t('tools.orderStatus'),
      'Request_return_order': t('tools.Request_return_order'),
      'Retrieve_customer_order_by_ID': t('tools.Retrieve_customer_order_by_ID'),
      'Request_exchange_shopify': t('tools.Request_exchange_shopify'),
      'Get_all_product_variants': t('tools.Get_all_product_variants')
    };
    return toolTranslations[toolName] || toolName;
  };

  const handleStartEditing = () => {
    setIsEditing(true);
    setEditedName(name);
    setEditedPrompt(prompt);
    setEditedToolIds(selectedToolIds);
  };

  const handleCancelEditing = () => {
    setIsEditing(false);
    setEditedName(name);
    setEditedPrompt(prompt);
    setEditedToolIds(selectedToolIds);
  };

  const handlePromptClick = () => {
    setShowPromptModal(true);
  };

  const handleClosePromptModal = () => {
    setShowPromptModal(false);
  };

  const handleChatBubbleToggle = () => {
    setShowChatBubble(!showChatBubble);
  };

  const handleDeleteConversation = async () => {
    if (!user) return;
    
    try {
      // Call the delete function from the ChatbotTest component
      if (chatbotTestRef.current) {
        await chatbotTestRef.current.deleteConversation();
      }
    } catch (error) {
      console.error('Error deleting conversation:', error);
    }
  };

  const ToolsOverlay = ({ show, onClose, availableTools, selectedToolIds, onToolToggle }) => {
    if (!show) return null;

    return (
      <div className="modalai tools-overlay">
        <div className="modal-content">
          <h3>{t('aiAssistant.selectTools')}</h3>
          <div className="tools-overlay-grid">
            {availableTools.map((tool) => (
              <div key={tool.id} className="ai-agent-tool-item">
                <input
                  type="checkbox"
                  id={`tool-${tool.id}`}
                  checked={selectedToolIds.includes(tool.id)}
                  onChange={(e) => onToolToggle(tool.id, e.target.checked)}
                />
                <label htmlFor={`tool-${tool.id}`}>{getToolTranslation(tool.name)}</label>
              </div>
            ))}
          </div>
          <div className="modal-actions">
            <Button variant="secondary" onClick={onClose}>
              {t('aiAssistant.close')}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  if (user === undefined || isLoading) {
    return (
      <div className="una-dashboard-loading-screen">
        <div className="una-loading-content">
          <div className="una-loading-logo">
            <img src="/bgless_logo.png" alt="Unanimity AI Logo" />
          </div>
          <h2>Loading AI Assistants</h2>
          <div className="una-loading-progress">
            <div className="una-progress-bar">
              <div className="una-progress-glow"></div>
            </div>
          </div>
          <div className="una-loading-metrics">
            <div className="una-metric-item">
              <FaRobot />
              <span>Fetching Assistants</span>
            </div>
            <div className="una-metric-item">
              <FaTools />
              <span>Loading Tools</span>
            </div>
            <div className="una-metric-item">
              <FaCode />
              <span>Processing Configurations</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (user === null && !isLoading) { // Check !isLoading to avoid premature redirect
     // Navigation is handled in useEffect [user, navigate]
    return <div className="error-state">Error loading user data or unauthorized. Redirecting...</div>; // Fallback UI
  }

  return (
    <div className="ai-assistant-container">
      <div className="panels-wrapper">
        <div className="left-panel">
          <div className="agent-selector">
            <div className="agent-circle">
              <FaRobot />
            </div>
            <div className="agent-info">
              <span className="agent-name">
                {selectedAssistant ? selectedAssistant.name : t('aiAssistant.selectAgent')}
              </span>
            </div>
          </div>

          {selectedAssistant && (
            <div className="agent-details">
              
              {isEditing && (
                <div className="form-group">
                  <label htmlFor="name">{t('aiAssistant.name')}:</label>
                  <input
                    type="text"
                    id="name"
                    value={editedName}
                    onChange={(e) => setEditedName(e.target.value)}
                    className="ai-agent-input"
                  />
                </div>
              )}
              <div className="ai-agent-tools-section">
                <div className="tools-header">
                {!isEditing && (
                  <Button 
                    variant="tertiary"
                    className="edit-button"
                    onClick={handleStartEditing}
                  >
                    <FaPen /> {t('aiAssistant.edit')}
                  </Button>
                )}
                  <h4>{t('aiAssistant.tools')}</h4>
                  {isEditing && (
                    <Button 
                      variant="tertiary"
                      className="add-tools-button"
                      onClick={() => setShowToolsOverlay(true)}
                    >
                      <FaPlus /> {t('aiAssistant.addTools')}
                    </Button>
                  )}
                </div>
                <div className="active-tools-grid">
                  {availableTools
                    .filter(tool => selectedToolIds.includes(tool.id))
                    .map((tool) => (
                      <div key={tool.id} className="ai-agent-tool-item">
                        <div className="tool-status">
                          <FaCheck className="tool-check" />
                        </div>
                        <label>{getToolTranslation(tool.name)}</label>
                      </div>
                    ))}
                </div>
              </div>
              <div className="form-group-ai">
                <label htmlFor="prompt">{t('aiAssistant.prompt')}:</label>
                <div 
                  className="ai-agent-value prompt-value clickable" 
                  onClick={handlePromptClick}
                >
                  {getPromptPreview(prompt)}
                </div>
              </div>


              {isEditing && (
                <div className="modal-actions">
                  <Button variant="primary" onClick={handleUpdateAIAssistant}>
                    {t('aiAssistant.save')}
                  </Button>
                  <Button variant="secondary" onClick={handleCancelEditing}>
                    {t('aiAssistant.cancel')}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="right-panel">
          <div className="tabs-section">
            <div className="tabs">
              <Button
                variant="tertiary"
                className={`tab-button ${activeTab === 'carriersInstructions' ? 'active' : ''}`}
                onClick={() => setActiveTab('carriersInstructions')}
              >
                <FaTruck /> {t('aiAssistant.carriersInstructionsTab')}
              </Button>
              {user?.edn_admin && (
                <Button
                  variant="tertiary"
                  className={`tab-button ${activeTab === 'clientsInstructions' ? 'active' : ''}`}
                  onClick={() => setActiveTab('clientsInstructions')}
                >
                  <FaUsers /> {t('aiAssistant.clientsInstructionsTab')}
                </Button>
              )}
              {user?.edn_admin && (
                <Button
                  variant="tertiary"
                  className={`tab-button ${activeTab === 'warehousesInstructions' ? 'active' : ''}`}
                  onClick={() => setActiveTab('warehousesInstructions')}
                >
                  <FaWarehouse /> {t('aiAssistant.warehousesInstructionsTab')}
                </Button>
              )}
              <Button
                variant="tertiary"
                className={`tab-button ${activeTab === 'knowledge' ? 'active' : ''}`}
                onClick={() => setActiveTab('knowledge')}
              >
                <FaBook /> {t('aiAssistant.knowledgeBoard')}
              </Button>
              <Button
                variant="tertiary"
                className={`tab-button ${activeTab === 'codeSnippets' ? 'active' : ''}`}
                onClick={() => setActiveTab('codeSnippets')}
              >
                <FaCode /> {t('codeSnippets.tabTitle')}
              </Button>
            </div>
            <div className="tab-content">
              {renderTabContent()}
            </div>
          </div>
        </div>
      </div>

      {/* Floating Chat Bubble */}
      <div className="floating-chat-bubble">
        <button 
          className="chat-toggle-button"
          onClick={handleChatBubbleToggle}
          aria-label="Toggle chat"
        >
          {showChatBubble ? <FaTimes /> : <FaCommentDots />}
        </button>
        
        {showChatBubble && (
          <div className="chat-bubble-container">
            <div className="chat-bubble-header">
              <span className="chat-bubble-title">
                {selectedAssistant?.name || 'AI Assistant'}
              </span>
              <div className="chat-bubble-controls">
                <button 
                  className="chat-delete-button"
                  onClick={handleDeleteConversation}
                  aria-label="Delete conversation"
                >
                  <FaTrash />
                </button>
                <button 
                  className="chat-close-button"
                  onClick={handleChatBubbleToggle}
                  aria-label="Close chat"
                >
                  <FaTimes />
                </button>
              </div>
            </div>
            <div className="chat-bubble-content">
              <ChatbotTest 
                ref={chatbotTestRef}
                assistantId={selectedAssistant?._id}
                assistantName={selectedAssistant?.name}
              />
            </div>
          </div>
        )}
      </div>

      {/* Agent Selector Modal */}
      {showAgentSelector && (
        <div className="modalai">
          <div className="modal-content agent-selector-modal">
            <h3>{t('aiAssistant.selectOrCreate')}</h3>
            <div className="agent-list">
              {aiAssistants.map((assistant) => (
                <div
                  key={assistant._id}
                  className={`agent-item ${assistant.is_running ? 'running' : ''}`}
                >
                  <div 
                    className="agent-item-content"
                    onClick={() => {
                      handleSelectAssistant(assistant);
                      setShowAgentSelector(false);
                    }}
                  >
                    <div className="agent-circle small">
                      <FaRobot />
                    </div>
                    <div className="agent-item-info">
                      <span className="agent-item-name">{assistant.name}</span>
                    </div>
                  </div>
                  <Button
                    variant={assistant.is_running ? "primary" : "tertiary"}
                    className={`run-button ${assistant.is_running ? 'running' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSetRunning(assistant._id);
                    }}
                  >
                    <FaPlay /> {assistant.is_running ? t('aiAssistant.running') : t('aiAssistant.run')}
                  </Button>
                </div>
              ))}
            </div>
            <div className="modal-actions">
              <Button
                variant="primary"
                onClick={() => {
                  setShowAgentSelector(false);
                  setShowCreateAgentModal(true);
                }}
              >
                <FaPlus /> {t('aiAssistant.createNew')}
              </Button>
              <Button
                variant="secondary"
                onClick={() => setShowAgentSelector(false)}
              >
                {t('aiAssistant.cancel')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Create Agent Modal */}
      {showCreateAgentModal && (
        <div className="ai-agent-modal">
          <div className="ai-agent-modal-content">
            <h3>{t('aiAssistant.createAssistant')}</h3>
            <div className="form-group">
              <label htmlFor="name">{t('aiAssistant.name')}:</label>
              <input
                type="text"
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="ai-agent-input"
              />
            </div>
            <div className="form-group">
              <label htmlFor="prompt">{t('aiAssistant.prompt')}:</label>
              <textarea
                id="prompt"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                rows="4"
                className="ai-agent-textarea"
              />
            </div>
            <div className="ai-agent-tools-section">
              <h4>{t('aiAssistant.selectTools')}</h4>
              <div className="ai-agent-tools-grid">
                {availableTools.map((tool) => (
                  <div key={tool.id} className="ai-agent-tool-item">
                    <input
                      type="checkbox"
                      id={`tool-${tool.id}`}
                      checked={selectedToolIds.includes(tool.id)}
                      onChange={(e) => {
                        const toolId = tool.id;
                        if (e.target.checked) {
                          setSelectedToolIds(prev => [...prev, toolId]);
                        } else {
                          setSelectedToolIds(prev => prev.filter(id => id !== toolId));
                        }
                      }}
                    />
                    <label htmlFor={`tool-${tool.id}`}>{getToolTranslation(tool.name)}</label>
                  </div>
                ))}
              </div>
            </div>
            <div className="modal-actions">
              <Button
                variant="primary"
                onClick={() => {
                  handleCreateAIAssistant();
                  setShowCreateAgentModal(false);
                }}
              >
                {t('aiAssistant.create')}
              </Button>
              <Button
                variant="secondary"
                onClick={() => setShowCreateAgentModal(false)}
              >
                {t('aiAssistant.cancel')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {showPromptModal && (
        <div className="modalai">
          <div className="modal-content prompt-modal">
            <h3>{t('aiAssistant.prompt')}</h3>
            {isEditing ? (
              <textarea
                value={editedPrompt}
                onChange={(e) => setEditedPrompt(e.target.value)}
                rows="15"
                className="ai-agent-textarea"
              />
            ) : (
              <div className="prompt-preview-content">
                {prompt}
              </div>
            )}
            <div className="modal-actions">
              {isEditing && (
                <Button 
                  variant="primary" 
                  onClick={handleUpdateAIAssistant}
                >
                  {t('aiAssistant.save')}
                </Button>
              )}
              <Button 
                variant="secondary" 
                onClick={handleClosePromptModal}
              >
                {t('aiAssistant.close')}
              </Button>
            </div>
          </div>
        </div>
      )}

      <ToolsOverlay 
        show={showToolsOverlay}
        onClose={() => setShowToolsOverlay(false)}
        availableTools={availableTools}
        selectedToolIds={isEditing ? editedToolIds : selectedToolIds}
        onToolToggle={(toolId, checked) => {
          if (checked) {
            setEditedToolIds([...editedToolIds, toolId]);
          } else {
            setEditedToolIds(editedToolIds.filter(id => id !== toolId));
          }
        }}
      />
    </div>
  );
};

export default AIAssistantManagement;
